import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AreaField, Panel, TextField } from "@/components/studio/Fields";
import { Markdown } from "@/components/site/Markdown";
import { errorMessage } from "@/lib/api";
import {
  deleteArticle,
  generateArticles,
  ownerArticle,
  ownerArticles,
  updateArticle,
} from "@/lib/site";

/* Four articles a week is a cadence, not a machine. The generator drafts; the
   owner reads, corrects and decides when it goes out. Nothing written here is
   public until its status says so. */

const STATUS_LABELS = {
  draft: "Brouillon",
  scheduled: "Programmé",
  published: "Publié",
};

const STATUS_COLOURS = {
  draft: "var(--muted-ink)",
  scheduled: "var(--warning)",
  published: "var(--success)",
};

/** Convert between the ISO stamp the API stores and what a date-time input wants. */
const toInput = (iso) => (iso ? iso.slice(0, 16) : "");
const fromInput = (value) => (value ? new Date(value).toISOString() : "");

const Generator = ({ onDone }) => {
  const [brief, setBrief] = useState({
    video_url: "",
    notes: "",
    topic: "",
    audience: "",
    tone: "",
    words: 800,
    languages: ["fr"],
  });
  const [busy, setBusy] = useState(false);

  const toggleLanguage = (code) =>
    setBrief((current) => {
      const next = current.languages.includes(code)
        ? current.languages.filter((lang) => lang !== code)
        : [...current.languages, code];
      // unticking the last language would ask for an article in no language
      return { ...current, languages: next.length ? next : current.languages };
    });

  const run = async () => {
    if (brief.notes.trim().length < 20) {
      toast.error("Écrivez d’abord quelques notes : sans matière, l’article sera creux.");
      return;
    }
    setBusy(true);
    try {
      const result = await generateArticles({
        ...brief,
        languages: brief.languages.length ? brief.languages : ["fr"],
      });
      toast.success(
        `${result.articles.length} brouillon${result.articles.length > 1 ? "s" : ""} écrit${
          result.articles.length > 1 ? "s" : ""
        }`
      );
      onDone(result.articles[0]?.id);
    } catch (error) {
      toast.error(errorMessage(error, "L’article n’a pas pu être écrit"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Écrire à partir d’une vidéo"
      hint="Collez le lien de la vidéo et jetez vos notes en vrac — même mal écrites. L’article accompagne la vidéo, il ne la transcrit pas."
    >
      <TextField
        label="Lien de la vidéo"
        hint="YouTube ou Vimeo. La vignette devient l’image de l’article."
        value={brief.video_url}
        onChange={(value) => setBrief({ ...brief, video_url: value })}
        placeholder="https://youtu.be/…"
        testId="brief-video"
      />
      <AreaField
        label="Vos notes"
        hint="Ce que vous avez fait, ce qui a raté, ce que vous referiez autrement. C’est la seule matière dont dispose le rédacteur."
        value={brief.notes}
        onChange={(value) => setBrief({ ...brief, notes: value })}
        rows={8}
        testId="brief-notes"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          label="Angle"
          hint="Facultatif. Sinon il en choisit un dans vos notes."
          value={brief.topic}
          onChange={(value) => setBrief({ ...brief, topic: value })}
          testId="brief-topic"
        />
        <TextField
          label="Pour qui"
          value={brief.audience}
          onChange={(value) => setBrief({ ...brief, audience: value })}
          testId="brief-audience"
        />
        <TextField
          label="Ton"
          value={brief.tone}
          onChange={(value) => setBrief({ ...brief, tone: value })}
          placeholder="direct, à la première personne"
          testId="brief-tone"
        />
        <TextField
          label="Longueur"
          hint="En mots."
          value={String(brief.words)}
          onChange={(value) => setBrief({ ...brief, words: Number(value) || 800 })}
          testId="brief-words"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="text-[11px] uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
          Langues
        </span>
        {["fr", "en"].map((code) => (
          <label key={code} className="flex items-center gap-1.5 text-[13px]">
            <input
              type="checkbox"
              checked={brief.languages.includes(code)}
              onChange={() => toggleLanguage(code)}
              data-testid={`brief-lang-${code}`}
              className="h-4 w-4"
            />
            {code.toUpperCase()}
          </label>
        ))}
        <span className="text-[11.5px] text-[color:var(--muted-ink)]">
          Deux langues = deux articles liés, pas une traduction automatique.
        </span>
      </div>

      <Button onClick={run} disabled={busy} className="mt-5" data-testid="generate-article">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
        {busy ? "Rédaction en cours…" : "Écrire le brouillon"}
      </Button>
      {busy ? (
        <p className="mt-2 text-[11.5px] text-[color:var(--muted-ink)]">
          Comptez une minute par langue. Ne fermez pas l’onglet.
        </p>
      ) : null}
    </Panel>
  );
};

const Editor = ({ article, onSaved, onDeleted }) => {
  const [draft, setDraft] = useState(article);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => setDraft(article), [article]);

  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setBusy(true);
    try {
      const saved = await updateArticle(draft.id, draft);
      setDraft(saved);
      toast.success("Article enregistré");
      onSaved(saved);
    } catch (error) {
      toast.error(errorMessage(error, "Enregistrement impossible"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer « ${draft.title} » ? C’est définitif.`)) return;
    await deleteArticle(draft.id);
    toast.success("Article supprimé");
    onDeleted(draft.id);
  };

  return (
    <Panel
      title={draft.title || "Sans titre"}
      hint={`/blog/${draft.slug}`}
      actions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPreview(!preview)}
            className="rounded-full border px-3 py-1.5 text-[12px]"
            style={{ borderColor: "var(--line)" }}
            data-testid="toggle-preview"
          >
            {preview ? "Éditer" : "Aperçu"}
          </button>
          <Button onClick={save} disabled={busy} size="sm" data-testid="save-article">
            {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
            Enregistrer
          </Button>
          <button
            type="button"
            onClick={remove}
            aria-label="Supprimer"
            className="rounded-full border p-2"
            style={{ borderColor: "var(--line)", color: "var(--danger)" }}
            data-testid="delete-article"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      }
    >
      {preview ? (
        <div className="rounded-lg border p-6" style={{ borderColor: "var(--line)" }}>
          <h1 className="text-[32px] font-semibold leading-tight">{draft.title}</h1>
          <p className="mt-3 text-[16px] text-[color:var(--ink-soft)]">{draft.excerpt}</p>
          <Markdown source={draft.body_md} className="mt-6 text-[15px]" />
        </div>
      ) : (
        <>
          <TextField label="Titre" value={draft.title} onChange={(v) => set("title", v)} testId="article-title" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Adresse"
              hint="La partie après /blog/. Changez-la et l’ancien lien ne fonctionne plus."
              value={draft.slug}
              onChange={(v) => set("slug", v)}
              testId="article-slug"
            />
            <TextField
              label="Lien de la vidéo"
              value={typeof draft.video === "string" ? draft.video : draft.video?.url || ""}
              onChange={(v) => set("video", v)}
              testId="article-video"
            />
          </div>
          <AreaField
            label="Chapeau"
            hint="Deux phrases. C’est ce qui s’affiche sur la carte et dans l’aperçu de partage."
            value={draft.excerpt}
            onChange={(v) => set("excerpt", v)}
            rows={2}
            testId="article-excerpt"
          />
          <AreaField
            label="Corps"
            hint="Markdown : ## pour un intertitre, - pour une liste, **gras**."
            value={draft.body_md}
            onChange={(v) => set("body_md", v)}
            rows={20}
            testId="article-body"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <TextField
              label="Étiquettes"
              hint="Séparées par des virgules."
              value={(draft.tags || []).join(", ")}
              onChange={(v) => set("tags", v.split(",").map((tag) => tag.trim()).filter(Boolean))}
              testId="article-tags"
            />
            <div className="mt-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
                État
              </p>
              <select
                value={draft.status}
                onChange={(event) => set("status", event.target.value)}
                data-testid="article-status"
                className="mt-1.5 h-10 w-full rounded-lg border bg-white px-2 text-[13.5px]"
                style={{ borderColor: "var(--line)" }}
              >
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
                Date de parution
              </p>
              <input
                type="datetime-local"
                value={toInput(draft.publish_at)}
                onChange={(event) => set("publish_at", fromInput(event.target.value))}
                data-testid="article-publish-at"
                className="mt-1.5 h-10 w-full rounded-lg border bg-white px-2 text-[13.5px]"
                style={{ borderColor: "var(--line)" }}
              />
            </div>
          </div>
          <p className="mt-3 text-[11.5px] text-[color:var(--muted-ink)]">
            Un article publié dont la date est dans le futur reste invisible jusqu’à cette
            date : il n’y a rien à programmer, il apparaît tout seul.
          </p>
        </>
      )}
    </Panel>
  );
};

export const JournalPanel = () => {
  const [articles, setArticles] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async (selectId) => {
    const data = await ownerArticles();
    setArticles(data.articles || []);
    setSchedule(data.schedule || null);
    if (selectId) setSelected(await ownerArticle(selectId));
    setLoading(false);
  };

  useEffect(() => {
    refresh().catch(() => setLoading(false));
  }, []);

  const open = async (id) => setSelected(await ownerArticle(id));

  const weeks = Object.entries(schedule?.by_week || {});

  return (
    <div className="space-y-6">
      <Generator onDone={(id) => refresh(id)} />

      <Panel
        title="Les articles"
        hint={
          schedule
            ? `Objectif : ${schedule.target_per_week} par semaine. Le compte ci-dessous vous dit où vous en êtes, il ne publie rien à votre place.`
            : undefined
        }
      >
        {weeks.length ? (
          <div className="mb-5 flex flex-wrap gap-2">
            {weeks.map(([week, count]) => (
              <span
                key={week}
                className="font-num rounded-full px-2.5 py-1 text-[11.5px]"
                style={{
                  backgroundColor: "var(--paper-2)",
                  color: count >= (schedule?.target_per_week || 4) ? "var(--success)" : "var(--ink-soft)",
                }}
              >
                {week} · {count}
              </span>
            ))}
          </div>
        ) : null}

        {loading ? (
          <p className="flex items-center gap-2 text-[13px] text-[color:var(--muted-ink)]">
            <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
          </p>
        ) : articles.length ? (
          <ul className="divide-y" style={{ borderColor: "var(--line)" }}>
            {articles.map((article) => (
              <li key={article.id}>
                <button
                  type="button"
                  onClick={() => open(article.id)}
                  data-testid={`article-row-${article.id}`}
                  className="flex w-full items-center justify-between gap-4 py-3 text-left"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-medium">{article.title}</span>
                    <span className="font-num text-[11px] text-[color:var(--muted-ink)]">
                      {article.lang.toUpperCase()} · /{article.slug} ·{" "}
                      {(article.publish_at || "").slice(0, 10) || "sans date"}
                    </span>
                  </span>
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase"
                    style={{
                      backgroundColor: "var(--paper-2)",
                      color: STATUS_COLOURS[article.status],
                    }}
                  >
                    {STATUS_LABELS[article.status]}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[13px] text-[color:var(--ink-soft)]">
            Aucun article pour l’instant.
          </p>
        )}
      </Panel>

      {selected ? (
        <Editor
          article={selected}
          onSaved={() => refresh(selected.id)}
          onDeleted={() => {
            setSelected(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
};

export default JournalPanel;
